import { RefObject, useEffect } from "react";

import { deepActiveElement, isEditableTarget } from "@tsmono/util";

import type { VirtualListHandle } from "../virtual/types";

interface ListKeyboardNavigationOptions {
  listHandle: RefObject<VirtualListHandle | null>;
  /** Scroll container — required for jump-to-top/bottom (preferred path). */
  scrollRef?: RefObject<HTMLDivElement | null>;
  /** Total number of items in the list. */
  itemCount: number;
  /**
   * When true, all shortcuts stand down so another surface can own the
   * keyboard (e.g. find-in-page is open).
   */
  disabled?: boolean;
}

/**
 * Generic list keyboard navigation: modifier+ArrowUp/Home jumps to the start,
 * modifier+ArrowDown/End to the end. Domain-specific keys (e.g. the
 * transcript's vim-style j/k turn stepping) belong in consumer-owned wrappers
 * such as `useTranscriptKeyboardNavigation`.
 */
export function useListKeyboardNavigation({
  listHandle,
  scrollRef,
  itemCount,
  disabled,
}: ListKeyboardNavigationOptions): void {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (disabled) return;
      // Only the visible list may claim these window-capture keys, so a
      // hidden/offscreen instance mounted elsewhere doesn't swallow them.
      const container = scrollRef?.current;
      if (
        container &&
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- intentional: checkVisibility is absent in jsdom and older browsers
        (!container.isConnected || container.checkVisibility?.() === false)
      ) {
        return;
      }
      // Meta/Ctrl only — Alt+Arrow chords belong to the browser/OS.
      const hasModifier = event.metaKey || event.ctrlKey;

      const isUp =
        (event.key === "ArrowUp" && hasModifier) ||
        (event.key === "Home" && hasModifier);
      const isDown =
        (event.key === "ArrowDown" && hasModifier) ||
        (event.key === "End" && hasModifier);
      if (!isUp && !isDown) return;

      if (isEditableTarget(deepActiveElement())) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const handle = listHandle.current;

      if (isUp) {
        if (handle) {
          handle.jumpToStart();
        } else {
          scrollRef?.current?.scrollTo({ top: 0 });
        }
      } else {
        if (handle) {
          handle.jumpToEnd();
        } else {
          const el = scrollRef?.current;
          if (el) el.scrollTop = el.scrollHeight;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [listHandle, scrollRef, itemCount, disabled]);
}
